// ═══════════════════════════════════════════════════════════════════
// UTILIDADES — Cotizador Low Travel
// Funciones puras sin side effects. Toda la lógica de cálculo y
// construcción de URLs vive aquí, separada de los componentes.
// ═══════════════════════════════════════════════════════════════════

import RUTAS from "../config/tarifas_traslados.json"
import { ORIGENES, DESTINOS_COTIZADOR, PN_BASE_URL, PN_DEFAULT_PARAMS, FDE, COSTO_OP_POR_PERSONA, MARGEN_DEFAULT, PRIVATE_PREMIUM_MAX_USD } from "../config/cotizador"

// ─── Fechas y noches ──────────────────────────────────────────────────

/**
 * Calcula la cantidad de noches entre dos fechas ISO (YYYY-MM-DD).
 * Retorna null si alguna fecha falta o el resultado es inválido.
 */
export function calcNights(dateStart, dateEnd) {
  if (!dateStart || !dateEnd) return null
  const diff = new Date(dateEnd) - new Date(dateStart)
  const noches = Math.round(diff / (1000 * 60 * 60 * 24))
  return noches > 0 ? noches : null
}

/**
 * Suma N noches a una fecha ISO y devuelve la nueva fecha ISO.
 * Útil para inferir fechaRegreso a partir de fechaSalida + noches.
 */
export function addNights(dateStr, nights) {
  if (!dateStr || !nights || nights <= 0) return null
  const d = new Date(dateStr)
  d.setDate(d.getDate() + parseInt(nights))
  return d.toISOString().split("T")[0]
}

/**
 * Formatea una fecha ISO (YYYY-MM-DD) a "DD/MM" para el itinerario visual.
 */
export function fmtDDMM(dateStr) {
  if (!dateStr) return "?"
  const [, m, d] = dateStr.split("-")
  return `${d}/${m}`
}

// ─── Destinos y orígenes ──────────────────────────────────────────────

/**
 * Devuelve la configuración completa de un destino por nombre.
 */
export function getDestinoInfo(nombre) {
  return DESTINOS_COTIZADOR.find(d => d.nombre === nombre) || null
}

/**
 * Devuelve la configuración de un origen por nombre.
 */
export function getOrigenInfo(nombre) {
  return ORIGENES.find(o => o.nombre === nombre) || null
}

/**
 * Indica si un destino tiene múltiples opciones de aeropuerto (ej: Maragogi).
 * En ese caso, el vendedor debe seleccionar cuál usar antes de generar el link.
 */
export function destinoRequiereAeropuerto(destinoNombre) {
  const d = getDestinoInfo(destinoNombre)
  return !!(d?.ola?.options?.length)
}

/**
 * Devuelve los destinos compatibles como segundo destino en un viaje combinado.
 * "Compatible" = comparte aeropuerto con el primer destino (misma zona geográfica).
 * Si el destino1 no está configurado o no hay compatibles, devuelve todos.
 */
export function getDestinosCompatibles(destino1Nombre, aeropuerto1Seleccionado = null) {
  if (!destino1Nombre) return DESTINOS_COTIZADOR

  const d1 = getDestinoInfo(destino1Nombre)
  if (!d1) return DESTINOS_COTIZADOR.filter(d => d.nombre !== destino1Nombre)

  let codigosD1 = []
  if (d1.ola.code) {
    codigosD1 = [d1.ola.code]
  } else if (d1.ola.options?.length) {
    codigosD1 = aeropuerto1Seleccionado
      ? [d1.ola.options.find(o => o.aeropuerto === aeropuerto1Seleccionado)?.code].filter(Boolean)
      : d1.ola.options.map(o => o.code)
  }

  if (!codigosD1.length) return DESTINOS_COTIZADOR.filter(d => d.nombre !== destino1Nombre)

  const compatibles = DESTINOS_COTIZADOR.filter(d => {
    if (d.nombre === destino1Nombre) return false
    const codigosD = d.ola.code ? [d.ola.code] : (d.ola.options || []).map(o => o.code)
    return codigosD.some(code => codigosD1.includes(code))
  })

  return compatibles.length > 0
    ? compatibles
    : DESTINOS_COTIZADOR.filter(d => d.nombre !== destino1Nombre)
}

// ─── OLA ──────────────────────────────────────────────────────────────

/**
 * Genera el link de búsqueda de vuelos en OLA.
 *
 * Para viajes de 2 destinos: usar el aeropuerto del destino 1 (ida) con las
 * fechas completas del viaje (salida → regreso). OLA muestra el vuelo
 * redondo completo y el vendedor puede ajustar el tramo de regreso si es necesario.
 *
 * @param {string}  origenNombre       - Nombre del origen (debe existir en ORIGENES)
 * @param {string}  destino1Nombre     - Nombre del destino 1 (debe existir en DESTINOS_COTIZADOR)
 * @param {string}  fechaIda           - Fecha de salida ISO (YYYY-MM-DD)
 * @param {string}  fechaVuelta        - Fecha de regreso ISO (YYYY-MM-DD)
 * @param {number}  adultos            - Cantidad de adultos (default 2)
 * @param {number}  menores            - Cantidad de menores (default 0)
 * @param {string}  aeropuertoOverride - Código IATA para destinos con múltiples aeropuertos
 * @returns {string|null} URL de OLA o null si faltan datos obligatorios
 */
export function buildOlaUrl({
  origenNombre,
  destino1Nombre,
  fechaIda,
  fechaVuelta,
  adultos = 2,
  menores = 0,
  aeropuertoOverride = null,
}) {
  const origen = getOrigenInfo(origenNombre)
  if (!origen) return null

  const destino = getDestinoInfo(destino1Nombre)
  if (!destino) return null

  // Resolver el código OLA del destino
  let olaDestCode = destino.ola?.code

  // Para destinos con múltiples aeropuertos (ej: Maragogi), usar el override
  if (!olaDestCode && aeropuertoOverride) {
    const opt = destino.ola?.options?.find(o => o.aeropuerto === aeropuertoOverride)
    olaDestCode = opt?.code
  }

  if (!olaDestCode || !fechaIda || !fechaVuelta) return null

  const params = new URLSearchParams({
    des: `${origen.olaCode}.${olaDestCode}`,
    dte: `${fechaIda}.${fechaVuelta}`,
    pax: `${adultos}.${menores}.0`,
    usd: "1",
  })

  return `https://www.ola.com.ar/buscar/aereos?${params.toString()}`
}

// ─── PriceNavigator ──────────────────────────────────────────────────

const PN_QS = new URLSearchParams(PN_DEFAULT_PARAMS).toString()

/**
 * Genera el link de búsqueda de hoteles en PriceNavigator.
 *
 * ⚠️ PENDIENTE TÉCNICO — SEARCH_ID:
 * La URL completa de resultados de PriceNavigator tiene este formato:
 *   /v3/hotels/results/{SEARCH_ID}/{SLUG}/{ID}/{CHECKIN}/{CHECKOUT}/{PAX}
 *
 * El SEARCH_ID es generado por la plataforma al iniciar una búsqueda
 * (POST /v3/hotels-bff/rpc/hotels/search) y no puede derivarse
 * matemáticamente. Hasta resolverlo, esta función construye la URL SIN
 * ese segmento (/v3/hotels/{SLUG}/{ID}/...) que puede funcionar como
 * página del destino con los filtros comerciales ya aplicados.
 *
 * Tarea futura: integrar el endpoint POST documentado para obtener el
 * searchId y construir la URL de resultados completa.
 *
 * @returns {string|null} URL de PriceNavigator o null si faltan datos del destino
 */
export function buildPNUrl({
  destinoNombre,
  checkin,
  checkout,
  adultos = 2,
  menores = 0,
}) {
  const destino = getDestinoInfo(destinoNombre)
  if (!destino?.pn?.slug || !destino?.pn?.id) return null

  const { slug, id } = destino.pn
  const pax = menores > 0 ? `${adultos}-${menores}` : `${adultos}`

  if (!checkin || !checkout) {
    // Sin fechas: página del destino con los filtros comerciales aplicados
    return `${PN_BASE_URL}/v3/hotels/${slug}/${id}?${PN_QS}`
  }

  // Con fechas: URL lo más completa posible sin el SEARCH_ID (ver nota arriba)
  return `${PN_BASE_URL}/v3/hotels/${slug}/${id}/${checkin}/${checkout}/${pax}?${PN_QS}`
}

// ─── Traslados — motor de costo mínimo global ─────────────────────────

// Alias: mapea nombres del cotizador a los del JSON cuando difieren
const ALIAS_ORIG = { "SSA": "Salvador de Bahia" }
const ALIAS_DEST = { "Florianópolis": "Canasvieiras" }

// Normaliza: sin acentos, minúsculas, sin espacios extra
function norm(s) {
  return (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim()
}

// Busca la ruta en el JSON maestro para un par origen→destino.
// Aplica aliases y respeta bidireccionalidad.
function findRoute(origen, destino) {
  const os = [...new Set([origen, ALIAS_ORIG[origen], ALIAS_DEST[origen]].filter(Boolean))].map(norm)
  const ds = [...new Set([destino, ALIAS_DEST[destino], ALIAS_ORIG[destino]].filter(Boolean))].map(norm)

  for (const r of RUTAS.routes) {
    const ro = norm(r.origin)
    const rd = norm(r.destination)
    if (os.includes(ro) && ds.includes(rd)) return r
    if (r.bidirectional && os.includes(rd) && ds.includes(ro)) return r
  }
  return null
}

// ─── ETAPA A: DP puro sobre un subset de tarifas ──────────────────────
// per_person → sin límite de max_pax: puede aplicarse a cualquier cantidad
//              de pasajeros (N tickets individuales)
// per_vehicle → limitado a max_pax por vehículo; se pueden combinar varios
function _dp(pax, tariffs) {
  const valid = (tariffs || []).filter(t => {
    if (t.source !== "current") return false
    if (t.pricing_mode === "per_person")  return t.unit_price_usd != null && t.unit_price_usd > 0
    if (t.pricing_mode === "per_vehicle") return t.price_usd      != null && t.price_usd      > 0
    return false
  })
  if (!valid.length) return null

  const dp      = Array(pax + 1).fill(Infinity)
  const choices = Array(pax + 1).fill(null)
  dp[0] = 0

  for (let j = 1; j <= pax; j++) {
    for (const t of valid) {
      if (t.pricing_mode === "per_person") {
        // Sin restricción de max_pax: cubre cualquier número de personas
        for (let k = 1; k <= j; k++) {
          const total = dp[j - k] + k * t.unit_price_usd
          if (total < dp[j]) { dp[j] = total; choices[j] = { tarifa: t, pax: k } }
        }
      } else {
        // per_vehicle: respetar min_pax y max_pax como capacidad por unidad
        const kMin = t.min_pax || 1
        const kMax = Math.min(t.max_pax, j)
        if (kMin > j) continue
        for (let k = kMin; k <= kMax; k++) {
          const total = dp[j - k] + t.price_usd
          if (total < dp[j]) { dp[j] = total; choices[j] = { tarifa: t, pax: k } }
        }
      }
    }
  }

  if (!isFinite(dp[pax])) return null

  const desglose = []
  let rem = pax
  while (rem > 0) {
    const c    = choices[rem]
    const last = desglose[desglose.length - 1]
    if (last && last.tarifa === c.tarifa && last.paxPorVehiculo === c.pax) {
      last.cantVehiculos++
    } else {
      desglose.push({ tarifa: c.tarifa, paxPorVehiculo: c.pax, cantVehiculos: 1 })
    }
    rem -= c.pax
  }
  return { costoTotal: dp[pax], desglose }
}

// ─── ETAPA B: preferencia comercial ───────────────────────────────────
// Se compara la solución de mínimo costo global contra la mejor solución
// solo-privada. Si la diferencia es ≤ PRIVATE_PREMIUM_MAX_USD, se prefiere
// el privado por comodidad para el pasajero.
function minCostDP(pax, tariffs) {
  const solMin     = _dp(pax, tariffs)
  const solPrivate = _dp(pax, (tariffs || []).filter(t => t.modality === "privado"))

  if (!solMin)     return null   // sin solución posible
  if (!solPrivate) return solMin // no hay opción privada: usar el mínimo

  return (solPrivate.costoTotal - solMin.costoTotal) <= PRIVATE_PREMIUM_MAX_USD
    ? solPrivate
    : solMin
}

/**
 * Calcula los traslados necesarios para un itinerario usando el JSON maestro.
 * Encuentra la combinación de menor costo para cada segmento.
 * Retorna array de { label, origen, destino, segmento, tipo, monto, noConfigurado, desglose }
 */
export function calcTransfers({ destino1, destino2, aeropuerto1, aeropuerto2, numDestinos, adultos, menores }) {
  const totalPax = (adultos || 0) + (menores || 0)
  if (!destino1 || totalPax === 0) return []

  const d1Info = getDestinoInfo(destino1)
  const d2Info = destino2 ? getDestinoInfo(destino2) : null
  if (!d1Info) return []

  const aero1 = aeropuerto1 || d1Info.ola?.aeropuerto
  const aero2 = numDestinos === 2 && d2Info
    ? (aeropuerto2 || d2Info.ola?.aeropuerto || aero1)
    : aero1

  const segmentos = numDestinos === 1
    ? [
        { label:`${aero1} → ${destino1}`,   origen: aero1,    destino: destino1, segmento:"llegada"       },
        { label:`${destino1} → ${aero1}`,   origen: destino1, destino: aero1,    segmento:"regreso"       },
      ]
    : [
        { label:`${aero1} → ${destino1}`,   origen: aero1,    destino: destino1, segmento:"llegada"       },
        { label:`${destino1} → ${destino2}`, origen: destino1, destino: destino2, segmento:"interdestino" },
        { label:`${destino2} → ${aero2}`,   origen: destino2, destino: aero2,    segmento:"regreso"       },
      ]

  return segmentos.map(seg => {
    const route    = findRoute(seg.origen, seg.destino)
    const dpResult = route ? minCostDP(totalPax, route.current_tariffs) : null

    if (!dpResult) return { ...seg, monto:null, tipo:null, noConfigurado:true, desglose:[] }

    const tipos = [...new Set(dpResult.desglose.map(d => d.tarifa.modality))]
    return {
      ...seg,
      monto:         dpResult.costoTotal,
      tipo:          tipos.length === 1 ? tipos[0] : "mixto",
      noConfigurado: false,
      desglose:      dpResult.desglose,
    }
  })
}

// ─── Costos ───────────────────────────────────────────────────────────

/**
 * Calcula el desglose completo de costos de una cotización.
 * El precio del vuelo y de cada hotel son totales (no por persona).
 * Los traslados se toman de los calculados, con posibilidad de override manual.
 * La ganancia se calcula sobre el subtotal: precio = subtotal × (1 + margen/100).
 */
export function calcCosts({ vuelo, hoteles1 = [], hoteles2 = [], trasladosCalculados = [], trasladosManuales = {}, extras, adultos = 0, menores = 0, margen = MARGEN_DEFAULT }) {
  const n = s => Number(String(s || "0").replace(/,/g, "")) || 0

  const costoVuelos  = n(vuelo?.precioTotal)
  const costoHoteles = [...hoteles1, ...hoteles2].reduce((sum, h) => sum + n(h.precioTotal), 0)

  const costoTraslados = trasladosCalculados.reduce((sum, t) => {
    const key      = `${t.origen}→${t.destino}`
    const override = trasladosManuales[key]
    const monto    = (override !== undefined && override !== "") ? n(override) : (t.monto || 0)
    return sum + monto
  }, 0)

  const fde        = FDE
  const operativo  = COSTO_OP_POR_PERSONA * (adultos + menores)
  const excursiones= extras?.excursiones?.incluye ? n(extras.excursiones.monto) : 0
  const asistencia = extras?.asistencia?.incluye  ? n(extras.asistencia.monto)  : 0

  const subtotal    = costoVuelos + costoHoteles + costoTraslados + fde + operativo + excursiones + asistencia
  const ganancia    = subtotal * (margen / 100)
  const precioFinal = Math.round(subtotal + ganancia)

  return { costoVuelos, costoHoteles, costoTraslados, fde, operativo, excursiones, asistencia, subtotal, ganancia, precioFinal }
}